import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { clearCotacaoData } from '../../../store/cotacaoSlice';
import { iniciarNovaAdesao } from '../../../utils/adesaoFluxo';
import { OpcaoMenu, opcoesVisiveis } from '../menuOpcoes';

import ModalPrimeiroAcesso from '../../../components/modal/primeiro-acesso/ModalPrimeiroAcesso';
import ShellSaac from '../../../components/shell/ShellSaac';
import PageHeader from '../../../ui/composicao/PageHeader';
import Card from '../../../ui/primitivos/Card';
import Icone, { NomeIcone } from '../../../ui/primitivos/Icone';
import { decodeJwtPayload } from '../../../utils/jwt';

import { RootState } from '../../../store/store';

/**
 * Home — o hub do sistema (reescrita na at-55, frente A).
 *
 * ⚠️ A tela **renderiza sem backend** (spec 01 §6) e precisa continuar assim:
 * nenhuma chamada de API aqui. As únicas consultas da Home são as do
 * `PendenciasBanner`, que é montado no `App` e só existe para TI/ADMIN.
 *
 * ⚠️ Três desvios deliberados em relação ao protótipo `inspo/`:
 *   1. **8 opções, não 7** — ele não conhece "Acompanhamento de Oficina"
 *      (at-53, 08/09/2026), que é posterior a ele. A fonte é `menuOpcoes.ts`.
 *   2. **Sem emoji** — ele escreve "Olá, {nome} 👋"; a regra do projeto
 *      (06/09/2026) é emoji nunca, em nenhum texto de interface.
 *   3. **Uma grade, não duas** — ele duplica a lista inteira (`MenuGrid` para
 *      mobile + a grade inline do desktop). Aqui é uma árvore só, com o layout
 *      mudando por breakpoint.
 *
 * 📌 A chamada "O que vamos fazer?" vive no `PageHeader` (e não como um bloco
 * grande acima do conteúdo, como no protótipo) porque a spec 01 §7 exige que as
 * 8 opções sejam encontráveis **sem rolagem** em 360×640: a saudação e o título
 * grandes do protótipo consomem ~120px, que é justamente o que falta para a
 * última opção caber.
 */

/** `id` de `menuOpcoes.ts` → ícone do design system. */
const ICONE_POR_OPCAO: Record<string, NomeIcone> = {
  cotacoes: 'cotacoes',
  adesoes: 'adesoes',
  'substituicao-veicular': 'substituicao-veicular',
  'substituicao-titular': 'substituicao-titular',
  vistoria: 'vistoria',
  carteira: 'carteira',
  'acompanhamento-oficina': 'acompanhamento-oficina',
  ti: 'ti',
};

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [modalPrimeiroAcesso, setModalPrimeiroAcesso] = useState(false);

  const userType = useSelector((state: RootState) => state.auth.userType);
  const accessToken = useSelector((state: RootState) => state.auth.accessToken);

  // Mesma lista do drawer e da sidebar — as três leem `menuOpcoes.ts`.
  const opcoes = useMemo(() => opcoesVisiveis(userType), [userType]);

  const primeiroNome = useMemo(() => {
    if (!accessToken) return null;
    const payload = decodeJwtPayload(accessToken) as { nome?: string } | null;
    return payload?.nome?.trim().split(/\s+/)[0] ?? null;
  }, [accessToken]);

  const abrirOpcao = (opcao: OpcaoMenu) => {
    // Fluxo novo de cotação começa limpo.
    if (opcao.limparCotacao) dispatch(clearCotacaoData());
    // Adesão nova nasce limpa (at-38).
    if (opcao.limparAdesao) iniciarNovaAdesao(dispatch);
    navigate(opcao.rota);
  };

  useEffect(() => {
    if (localStorage.getItem('firstLogin') === 'true') setModalPrimeiroAcesso(true);
  }, []);

  return (
    <ShellSaac
      cabecalho={
        <PageHeader
          titulo="O que vamos fazer?"
          sobretitulo={primeiroNome ? `Olá, ${primeiroNome}` : undefined}
        />
      }
    >
      <div className="tw-mx-auto tw-w-full tw-max-w-5xl tw-px-0 tw-pb-8 lg:tw-px-8 lg:tw-py-8">
        <nav aria-label="Menu principal">
          {/*
            UMA lista para as duas larguras:
            • mobile  — card branco único, itens viram linhas separadas por borda
            • desktop — grade de cards independentes sobre o fundo neutro
          */}
          <ul
            className="tw-m-0 tw-list-none tw-overflow-hidden tw-rounded-t-3xl tw-bg-white tw-p-0 lg:tw-grid lg:tw-grid-cols-2 lg:tw-gap-4 lg:tw-overflow-visible lg:tw-rounded-none lg:tw-bg-transparent xl:tw-grid-cols-3"
          >
            {opcoes.map((opcao) => (
              <li key={opcao.id}>
                <Card
                  interativo
                  /* ⚠️ `aparencia="nenhuma"`: aqui a superfície muda com a
                     largura (linha de lista no mobile, card no desktop) e
                     sobrescrever raio/borda por className não é confiável em
                     Tailwind — ver o comentário em `Card.tsx`. */
                  aparencia="nenhuma"
                  onClick={() => abrirOpcao(opcao)}
                  rotuloAcessivel={`${opcao.titulo}: ${opcao.subtitulo}`}
                  className="tw-flex tw-h-full tw-min-h-[62px] tw-items-center tw-gap-3 tw-border-b tw-border-superficie-borda tw-bg-white tw-px-4 tw-py-3 lg:tw-min-h-0 lg:tw-flex-col lg:tw-items-start lg:tw-gap-4 lg:tw-rounded-2xl lg:tw-border lg:tw-p-6 lg:tw-shadow-sm"
                >
                  <span className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-xl tw-bg-brand-tint tw-text-brand lg:tw-h-11 lg:tw-w-11">
                    <Icone nome={ICONE_POR_OPCAO[opcao.id] ?? 'carteira'} tamanho={22} />
                  </span>

                  <span className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-0.5">
                    <span className="tw-font-marca tw-text-base tw-font-bold tw-leading-snug tw-text-texto-titulo">
                      {opcao.titulo}
                    </span>
                    <span className="tw-line-clamp-2 tw-text-xs tw-leading-snug tw-text-texto-suave lg:tw-text-sm">
                      {opcao.subtitulo}
                    </span>
                  </span>

                  {/* Afordância de clique do item inteiro. Some no desktop, onde
                      o card já tem hover e elevação próprios. */}
                  <span className="tw-shrink-0 tw-text-texto-desabilitado lg:tw-hidden" aria-hidden="true">
                    <Icone nome="chevron-direita" tamanho={18} />
                  </span>
                </Card>
              </li>
            ))}
          </ul>
        </nav>
      </div>

      <ModalPrimeiroAcesso
        isOpen={modalPrimeiroAcesso}
        onClose={() => setModalPrimeiroAcesso(false)}
        onSuccess={() => {
          setModalPrimeiroAcesso(false);
          localStorage.setItem('firstLogin', 'false');
        }}
      />
    </ShellSaac>
  );
};

export default HomePage;
